import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Flame, Loader2 } from "lucide-react";

/**
 * GitHub-style contribution grid.
 * Columns = weeks, rows = weekdays (Mon-Sun), cells colored by intensity of
 * tasks completed on that calendar date.
 */
const WEEKS = 22; // ~5 months on screen
const MS_DAY = 24 * 60 * 60 * 1000;

function fmtISODate(d) {
  return d.toISOString().slice(0, 10);
}

function intensity(count, max) {
  if (!count) return 0;
  if (max <= 1) return 4;
  const ratio = count / max;
  if (ratio >= 0.75) return 4;
  if (ratio >= 0.5) return 3;
  if (ratio >= 0.25) return 2;
  return 1;
}

const LEVEL_STYLE = [
  "bg-white/[0.04] border border-white/[0.04]", // 0
  "bg-primary/20 border border-primary/20",
  "bg-primary/40 border border-primary/30",
  "bg-primary/60 border border-primary/40",
  "bg-primary/90 border border-primary/50 shadow-[0_0_10px_rgba(0,255,180,0.25)]",
];

export default function StreakCalendar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/analytics/history", { params: { days: WEEKS * 7 } });
        if (!cancel) setData(data);
      } catch (e) {
        if (!cancel) toast.error(formatApiError(e.response?.data?.detail));
      } finally { if (!cancel) setLoading(false); }
    })();
    const onSwitch = () => {
      setLoading(true);
      api.get("/analytics/history", { params: { days: WEEKS * 7 } })
        .then(({ data }) => setData(data))
        .finally(() => setLoading(false));
    };
    window.addEventListener("adyap:goal-switched", onSwitch);
    return () => { cancel = true; window.removeEventListener("adyap:goal-switched", onSwitch); };
  }, []);

  const cells = useMemo(() => {
    // Build columns of 7 days from ~WEEKS weeks ago to today, ending on today.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // find the most recent Sunday >= today (col fills top→bottom Sun..Sat)
    const start = new Date(today.getTime() - (WEEKS * 7 - 1) * MS_DAY);
    // align to previous Sunday
    start.setDate(start.getDate() - start.getDay());
    const cols = [];
    const map = data?.days || {};
    const maxDay = data?.max_in_day || 1;
    for (let w = 0; w < WEEKS + 1; w++) {
      const col = [];
      for (let d = 0; d < 7; d++) {
        const dt = new Date(start.getTime() + (w * 7 + d) * MS_DAY);
        if (dt > today) { col.push(null); continue; }
        const key = fmtISODate(dt);
        const count = map[key] || 0;
        col.push({ key, count, level: intensity(count, maxDay), date: dt });
      }
      cols.push(col);
    }
    return cols;
  }, [data]);

  const monthLabels = useMemo(() => {
    // one label per column when the month changes
    if (!cells.length) return [];
    const out = [];
    let lastMonth = -1;
    cells.forEach((col, idx) => {
      const firstNonNull = col.find((c) => c);
      if (!firstNonNull) { out.push(null); return; }
      const m = firstNonNull.date.getMonth();
      if (m !== lastMonth) {
        lastMonth = m;
        out.push(firstNonNull.date.toLocaleString("en-US", { month: "short" }));
      } else {
        out.push(null);
      }
    });
    return out;
  }, [cells]);

  return (
    <div className="glass rounded-2xl p-5" data-testid="streak-calendar">
      <div className="flex items-start sm:items-center justify-between mb-4 flex-col sm:flex-row gap-2">
        <div>
          <div className="flex items-center gap-2 text-zinc-500 text-xs tracking-wider uppercase font-semibold">
            <Flame className="w-4 h-4 text-accent" /> Consistency
          </div>
          <p className="text-sm text-zinc-400 mt-1">
            {loading ? "Loading…" : (
              <>
                <span className="text-white font-medium">{data?.total_tasks_completed || 0}</span> tasks completed on <span className="text-white font-medium">{data?.active_days || 0}</span> days
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-zinc-500">
          <span>Less</span>
          {[0, 1, 2, 3, 4].map((lv) => (
            <span key={lv} className={`w-3 h-3 rounded-sm ${LEVEL_STYLE[lv]}`} />
          ))}
          <span>More</span>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : (
        <div className="overflow-x-auto pb-1">
          <div className="inline-flex flex-col min-w-full">
            <div className="flex items-end gap-[3px] pl-6 mb-1 h-4 text-[10px] text-zinc-500">
              {monthLabels.map((m, i) => (
                <div key={i} className="w-3 shrink-0 text-left">{m || ""}</div>
              ))}
            </div>
            <div className="flex gap-[3px]">
              <div className="flex flex-col gap-[3px] mr-1 text-[10px] text-zinc-500 pt-[1px]">
                {["", "Mon", "", "Wed", "", "Fri", ""].map((d, i) => (
                  <div key={i} className="h-3 w-4 leading-3">{d}</div>
                ))}
              </div>
              {cells.map((col, ci) => (
                <div key={ci} className="flex flex-col gap-[3px]">
                  {col.map((c, ri) => {
                    if (!c) return <div key={ri} className="w-3 h-3" />;
                    const dateStr = c.date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                    return (
                      <div
                        key={ri}
                        className={`w-3 h-3 rounded-sm ${LEVEL_STYLE[c.level]} hover:ring-2 hover:ring-primary/60 transition-shadow`}
                        title={`${c.count} task${c.count === 1 ? "" : "s"} · ${dateStr}`}
                        data-testid={`heatmap-cell-${c.key}`}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
